import React from 'react'
import { theme } from 'antd'
import { useIntl } from 'react-intl'
import { IConnectedGoogleCalendar, IConnectedOutlookCalendar } from '@vacationtracker/shared/types/calendar'
import { radius, spacing } from '@vacationtracker/shared/constants/ThemeSetting'
import { Banner } from '../../design-system/Banner'
import { Button } from '../../design-system/Button'
import { Stack } from '../../design-system/Stack'
import { StatusBadge } from '../../design-system/StatusBadge'
import { Text } from '../../design-system/Text'

export type CalendarConnectionStatus = 'disconnected' | 'connected' | 'expired'

export interface ICalendarConnection {
  key: string
  name: string
  icon: React.ReactNode
  status: CalendarConnectionStatus
  /** True from the Connect/Reconnect click until the calendar picker opens: sign-in and the calendar list take a moment. */
  isConnecting?: boolean
  /** Used by both Connect and Reconnect: reconnecting is connecting again. */
  onConnect: () => void
  onDisconnect: () => void
}

export interface ICalendarConnectionListProps {
  connections: ICalendarConnection[]
}

// A string is the id of a calendar connected in this session, before the page reloads the full record.
export function getCalendarConnectionStatus(calendar: IConnectedGoogleCalendar | IConnectedOutlookCalendar | string | null): CalendarConnectionStatus {
  if (!calendar) {
    return 'disconnected'
  }
  if (typeof calendar !== 'string' && calendar.error) {
    return 'expired'
  }
  return 'connected'
}

function CalendarConnectionRow({ connection }: { connection: ICalendarConnection }) {
  const { formatMessage } = useIntl()
  const { name, icon, status, isConnecting, onConnect, onDisconnect } = connection

  return (
    <Stack gap="sm">
      <Stack direction="horizontal" justify="space-between" align="center" gap="sm" wrap>
        <Stack direction="horizontal" align="center" gap="xs">
          {icon}
          <Text weight="bold">{name}</Text>
          {status === 'connected' && <StatusBadge tone="success">{formatMessage({ id: 'connectCalendar.statusConnected' })}</StatusBadge>}
          {status === 'expired' && <StatusBadge tone="warning">{formatMessage({ id: 'connectCalendar.statusExpired' })}</StatusBadge>}
        </Stack>
        <Stack direction="horizontal" gap="xs">
          {status === 'disconnected' && (
            <Button loading={isConnecting} onClick={onConnect}>
              {formatMessage({ id: 'connectCalendar.connectButton' })}
            </Button>
          )}
          {status !== 'disconnected' && (
            <Button variant="outline-danger" disabled={isConnecting} onClick={onDisconnect}>
              {formatMessage({ id: 'connectCalendar.disconnect' })}
            </Button>
          )}
          {status === 'expired' && (
            <Button variant="primary" loading={isConnecting} onClick={onConnect}>
              {formatMessage({ id: 'connectCalendar.reconnect' })}
            </Button>
          )}
        </Stack>
      </Stack>
      {status === 'expired' && <Banner tone="warning" message={formatMessage({ id: 'connectCalendar.reconnectBanner' })} />}
    </Stack>
  )
}

export function CalendarConnectionList({ connections }: ICalendarConnectionListProps) {
  const { token } = theme.useToken()
  const divider = `1px solid ${token.colorBorderSecondary}`

  return (
    <div style={{ border: divider, borderRadius: radius.base }}>
      {connections.map((connection, index) => (
        <div key={connection.key} style={{ padding: `${spacing.sm}px ${spacing.base}px`, borderTop: index > 0 ? divider : undefined }}>
          <CalendarConnectionRow connection={connection} />
        </div>
      ))}
    </div>
  )
}

export default CalendarConnectionList
